import { useCallback, useMemo, useState } from 'react';

import {
  useButtonPressedCallback,
  useLinkPressedCallback
} from '@zenbusiness/application-commons-clickstream';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import useBreakpoints from '@zenbusiness/zui/useBreakpoints';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';

import StatementsModal from '#client/components/StatementsModal';
import { FAQ_OVERVIEW_URL } from '#client/constants/FAQs';
import { MODAL_NAMES } from '#client/constants/modals';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import useModalContext from '#client/hooks/useModalContext';

import { MenuItemStyled, MenuStyled } from './MoreButtonV2.styles';

const CONTROLLER = 'more-menu';
const CONTROLLED_BY = 'show-more-menu';

export default function MoreButtonV2() {
  const { isDisable } = useDisableDashboardContext();
  const { openModal } = useModalContext();
  const {
    sizes: { isMobile }
  } = useBreakpoints();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Tracking Details: Tracking added to statements button
   * Question: How many user click the statements button?
   */
  const trackStatementPressed = useButtonPressedCallback({
    elementName: 'statements_button',
    elementText: 'Statements',
    appPageName: 'active_banking_dashboard'
  });

  const handleOpenStatements = useCallback(() => {
    trackStatementPressed();
    handleClose();
    openModal({
      component: StatementsModal
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

  const handleClickFAQs = useLinkPressedCallback({
    elementName: 'Banking_FAQ_link',
    elementText: 'Banking_FAQs',
    appPageName: 'active_banking_dashboard'
  });

  const handleOpenFAQs = useCallback(() => {
    handleClickFAQs();
    handleClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackCardsBtnPressed = useButtonPressedCallback({
    elementName: 'manage_cards_button',
    elementText: 'Card',
    appPageName: 'active_banking_dashboard'
  });

  const handleOpenCardModal = useCallback(() => {
    trackCardsBtnPressed();
    openModal({
      name: MODAL_NAMES.CARD_MODAL
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

  const menuItems = useMemo(() => {
    const listItem = [
      <MenuItemStyled
        disabled={isDisable}
        key="cards"
        onClick={handleOpenCardModal}
      >
        Card
      </MenuItemStyled>,
      <MenuItemStyled
        disabled={isDisable}
        key="statements"
        onClick={handleOpenStatements}
      >
        Statements
      </MenuItemStyled>,
      <MenuItemStyled
        component="a"
        disabled={isDisable}
        href={FAQ_OVERVIEW_URL}
        key="faqs"
        onClick={handleOpenFAQs}
        target="_blank"
      >
        FAQs
      </MenuItemStyled>
    ];

    // When screen size larger than mobile, remove Card option
    if (!isMobile) {
      listItem.shift();
    }

    // When screen size is too large, remove Statement option
    if (isXlUp) {
      listItem.shift();
    }

    return listItem;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisable, isMobile, isXlUp]);

  return (
    <>
      <Button
        aria-controls={open ? CONTROLLER : undefined}
        aria-expanded={open}
        aria-haspopup
        disabled={isDisable}
        id={CONTROLLED_BY}
        onClick={handleOpen}
        size="small"
        variant="outlined"
      >
        {isMobile ? <MoreVertOutlinedIcon /> : <MoreHorizOutlinedIcon />}
      </Button>
      <MenuStyled
        anchorEl={anchorEl}
        disableAutoFocus
        id={CONTROLLER}
        MenuListProps={{
          'aria-labelledby': CONTROLLED_BY
        }}
        onClose={(event) => handleClose('', event)}
        open={open}
      >
        <Stack gap="4px">{menuItems}</Stack>
      </MenuStyled>
    </>
  );
}
